import { useEffect, useRef } from 'react'
import { Animated, Easing, type View } from 'react-native'

import * as S from './control.styles'

import {
  DisplayNames,
  type ScopedProps,
  useInputMultiSelectContext,
} from '../../input-multi-select.context'

import { useLayoutObserver } from '~/hooks'

import { Button } from '~/components/button'
import { Icon, type TIconProps } from '~/components/icon'

import { InputMultiSelectChip } from '../chip'

import { useControlInteractionsSize } from './use-control-interactions-size'
import { useSelectedOptions } from './use-selected-options'

type InputMultiSelectControlProps = {
  leftIcon?: TIconProps['symbol']
  testID?: string
}

const CHEVRON_ANIMATION_DURATION = 150
const CHEVRON_ANIMATION_TO_VALUE_FOCUSED = 1
const CHEVRON_ANIMATION_TO_VALUE_BLURRED = 0
const CHEVRON_ANIMATION_EASING = Easing.linear

export const InputMultiSelectControl: React.FC<
  InputMultiSelectControlProps
> = externalProps => {
  const { __scopeInputMultiSelect, leftIcon, ...props } =
    externalProps as ScopedProps<InputMultiSelectControlProps>

  const {
    size,
    isFocused,
    isError,
    isDisabled,
    isReadOnly,
    handleControlFocusState,
    confirmedOptionsSelected,
    temporaryOptionsSelected,
    removeSelectedOption,
    setControlLayoutInfo,
  } = useInputMultiSelectContext(DisplayNames.CONTROL, __scopeInputMultiSelect)

  const inputMultiSelectControlRef = useRef<View>(null)
  const chevronRotation = useRef(new Animated.Value(0)).current

  const { measureLayout } = useLayoutObserver(inputMultiSelectControlRef)

  const { clearButtonSize, symbolSize } = useControlInteractionsSize(size)

  const { isFilled, hiddenOptionsCount } = useSelectedOptions([
    ...confirmedOptionsSelected,
    ...temporaryOptionsSelected,
  ])

  const iconColor = isDisabled
    ? 'colorTextNeutralDisabled'
    : isReadOnly
      ? 'colorTextNeutralReadonly'
      : 'colorTextNeutralDefault'

  const propsStates = {
    isFilled,
    isFocused,
    isError,
    isDisabled,
    isReadOnly,
  }

  function handleClearSelection() {
    removeSelectedOption()
    handleControlFocusState('blur')
  }

  function getLeftIconElement() {
    if (leftIcon) {
      return <Icon size={symbolSize} color={iconColor} symbol={leftIcon} />
    }

    return null
  }

  const showClearButton = isFilled && isFocused

  function measureLayoutComponent() {
    if (isFocused) {
      measureLayout(({ width, height, pageX, pageY }) => {
        setControlLayoutInfo({
          x: pageX,
          y: pageY + height,
          width,
          height,
        })
      })
    }
  }

  useEffect(() => {
    Animated.timing(chevronRotation, {
      toValue: isFocused
        ? CHEVRON_ANIMATION_TO_VALUE_FOCUSED
        : CHEVRON_ANIMATION_TO_VALUE_BLURRED,
      duration: CHEVRON_ANIMATION_DURATION,
      easing: CHEVRON_ANIMATION_EASING,
      useNativeDriver: true,
    }).start()

    measureLayoutComponent()
  }, [isFocused, chevronRotation])

  return (
    <S.Container
      {...props}
      {...propsStates}
      ref={inputMultiSelectControlRef}
      size={size}
      disabled={isDisabled || isReadOnly}
      onPress={() => handleControlFocusState('focus')}
      onLayout={measureLayoutComponent}
    >
      {getLeftIconElement()}

      <S.ContainerChosenOptions testID="input-multi-select-(container-chosen-options)">
        {[...confirmedOptionsSelected, ...temporaryOptionsSelected!]
          .slice(0, 3)
          .map(({ id, label }) => (
            <InputMultiSelectChip
              key={id}
              label={label}
              onLongPress={() => {
                removeSelectedOption(id)
              }}
            />
          ))}

        {hiddenOptionsCount > 0 && (
          <InputMultiSelectChip
            label={`+${hiddenOptionsCount}`}
            hiddenClearButton
          />
        )}
      </S.ContainerChosenOptions>

      <S.Interactions>
        <S.ClearButton size={clearButtonSize}>
          {showClearButton ? (
            <Button.Root
              variant="neutral-tertiary"
              size={clearButtonSize}
              onPress={handleClearSelection}
            >
              <Button.Icon symbol="rdicon-dismiss-circle-outline" />
            </Button.Root>
          ) : null}
        </S.ClearButton>

        <Animated.View
          style={{
            transform: [
              {
                rotate: chevronRotation.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '180deg'],
                }),
              },
            ],
          }}
        >
          <Icon
            symbol="rdicon-chevron-down"
            color={iconColor}
            size={symbolSize}
          />
        </Animated.View>
      </S.Interactions>
    </S.Container>
  )
}

InputMultiSelectControl.displayName = DisplayNames.CONTROL
